import React, { useCallback, useEffect, useState } from 'react';

import { Control, Controller, FieldError } from 'react-hook-form';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Box, Button, Chip, FormControl, FormHelperText, InputLabel, Stack } from '@mui/material';

const MAX_FILE_SIZE = 5_000_000; // 5MB

interface PDFUploadFieldProps {
  control: Control<Record<string, unknown>>;
  name: string;
  label?: string;
  error?: FieldError;
  disabled?: boolean;
  existingPdfData?: {
    pdf_filename?: string;
    pdf_s3_key?: string;
  };
}

export default function PDFUploadField({
  control,
  name,
  label = 'PDF Document',
  error,
  disabled,
  existingPdfData,
}: PDFUploadFieldProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [existingPdf, setExistingPdf] = useState<{ filename: string; downloadUrl: string } | null>(null);

  useEffect(() => {
    setExistingPdf(null);
    if (existingPdfData?.pdf_filename && existingPdfData?.pdf_s3_key) {
      setExistingPdf({
        filename: existingPdfData.pdf_filename,
        downloadUrl: existingPdfData.pdf_s3_key,
      });
    }
  }, [existingPdfData]);

  const convertFileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64Content = result.split(',')[1];
        resolve(base64Content);
      };
      reader.onerror = (error) => reject(error);
    });
  }, []);

  const handleFileChange = useCallback(
    async (file: File, onChange: (value: unknown) => void) => {
      if (!file) {
        onChange(null);
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        alert('File size exceeds the 5MB limit.');
        return;
      }

      setIsConverting(true);
      try {
        const base64Content = await convertFileToBase64(file);
        onChange({
          filename: file.name,
          content: base64Content,
          mimeType: 'application/pdf',
        });
        setSelectedFile(file);
      } catch (error) {
        console.error('Error converting file to base64:', error);
        alert('Error processing file. Please try again.');
      } finally {
        setIsConverting(false);
      }
    },
    [convertFileToBase64],
  );

  const handleRemoveFile = useCallback((onChange: (value: unknown) => void) => {
    setSelectedFile(null);
    setExistingPdf(null);
    onChange(null);
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Box sx={{ mt: 2, mb: 2 }}>
          <FormControl error={!!error} sx={{ width: '100%' }}>
            <InputLabel shrink>{label}</InputLabel>
            <Box sx={{ mt: 2 }}>
              <Stack spacing={2}>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  disabled={disabled || isConverting}
                  sx={{
                    width: 'fit-content',
                    minWidth: '140px',
                  }}
                >
                  {isConverting ? 'Processing...' : 'Upload PDF'}
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{ display: 'none' }}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        handleFileChange(file, onChange);
                      }
                      event.target.value = '';
                    }}
                  />
                </Button>

                {selectedFile && (
                  <Chip
                    icon={<PictureAsPdfIcon />}
                    label={selectedFile.name}
                    onDelete={() => handleRemoveFile(onChange)}
                    deleteIcon={<DeleteIcon />}
                    color="primary"
                    variant="outlined"
                    sx={{ width: 'fit-content' }}
                  />
                )}

                {existingPdf && !selectedFile && (
                  <Chip
                    icon={<PictureAsPdfIcon />}
                    label={existingPdf.filename}
                    onDelete={() => handleRemoveFile(onChange)}
                    deleteIcon={<DeleteIcon />}
                    color="primary"
                    variant="outlined"
                    clickable
                    onClick={() => window.open(existingPdf.downloadUrl, '_blank')}
                    sx={{ width: 'fit-content' }}
                  />
                )}

                {value && !selectedFile && !existingPdf && (
                  <Chip
                    icon={<PictureAsPdfIcon />}
                    label={(value as { filename?: string }).filename || 'PDF Document'}
                    onDelete={() => handleRemoveFile(onChange)}
                    deleteIcon={<DeleteIcon />}
                    color="primary"
                    variant="outlined"
                    sx={{ width: 'fit-content' }}
                  />
                )}

                {error && <FormHelperText>{error.message}</FormHelperText>}
              </Stack>
            </Box>
          </FormControl>
        </Box>
      )}
    />
  );
}
